import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/api/auth";
import type { UserRole } from "@/types";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
  roles?: UserRole[];
}

/**
 * Route guard — redirects to /login if not authenticated.
 * Preserves the intended destination via ?return_to so login
 * can redirect back after success.
 *
 * If `roles` is provided, also checks the user has at least one
 * of the allowed roles; redirects to /customer/dashboard if not.
 */
export default function ProtectedRoute({
  children,
  roles,
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // While checking token validity, show spinner
  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-ink-400" />
      </div>
    );
  }

  // Not logged in → redirect to login with return path
  if (!user) {
    const returnUrl = encodeURIComponent(
      location.pathname + location.search
    );
    return <Navigate to={`/login?return_to=${returnUrl}`} replace />;
  }

  // Role check — if user doesn't have an allowed role, send to dashboard
  if (roles && roles.length > 0 && !roles.includes(user.role as UserRole)) {
    return <Navigate to="/customer/dashboard" replace />;
  }

  return <>{children}</>;
}